/**
 * AriaCustomerArea — Shell unificato per il cliente Aria.
 * URL: /app/aria, /app/aria/installazione, /personalizza, /knowledge, /lead, /piano
 *
 * Layout: top bar con widget switcher · sidebar verticale 5 voci · outlet.
 */
import React, { useEffect, useState, useMemo, createContext, useContext } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    Sparkles, Code2, Palette, BookOpen, Inbox, CreditCard,
    ChevronDown, Plus, ArrowLeft, Check, AlertTriangle, Layers, Brain,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TIER_LABEL = { free: "Free", welcome: "Welcome", full: "Full", pro: "Full", cinematic: "Cinematic" };
const TIER_COLOR = { free: "#9EC3FF", welcome: "#86EFAC", full: "#A855F7", pro: "#A855F7", cinematic: "#FF1A5C" };

const NAV = [
    { to: "/app/aria/console", label: "Console", icon: Layers, desc: "Tutte le tue Aria" },
    { to: "/app/aria/account", label: "Brand & Policies", icon: Brain, desc: "Cervello condiviso" },
    { to: "/app/aria/installazione", label: "Installazione", icon: Code2, desc: "Snippet per il tuo sito" },
    { to: "/app/aria/personalizza", label: "Personalizza", icon: Palette, desc: "Persona, tono, colori" },
    { to: "/app/aria/knowledge", label: "Knowledge", icon: BookOpen, desc: "Cosa Aria deve sapere" },
    { to: "/app/aria/lead", label: "Lead & Chat", icon: Inbox, desc: "Conversazioni e contatti" },
    { to: "/app/aria/piano", label: "Piano", icon: CreditCard, desc: "Abbonamento e fatturazione" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Context: condivide widget selezionato + sub
// ─────────────────────────────────────────────────────────────────────────────
const AriaCustomerCtx = createContext(null);
export const useAriaCustomer = () => useContext(AriaCustomerCtx);

export default function AriaCustomerArea() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [widgets, setWidgets] = useState([]);
    const [activeToken, setActiveToken] = useState(null);
    const [config, setConfig] = useState(null);
    const [sub, setSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [switcherOpen, setSwitcherOpen] = useState(false);

    useEffect(() => {
        document.title = "Aria · Area Cliente";
        if (!user) { navigate("/login?next=/app/aria"); return; }
        (async () => {
            try {
                const [wRes, sRes] = await Promise.all([
                    api.get("/widget/me"),
                    api.get("/billing/me"),
                ]);
                const ws = wRes.data || [];
                setWidgets(ws);
                setSub(sRes.data);
                if (ws.length > 0) {
                    const saved = localStorage.getItem("aria.active_widget");
                    const initial = ws.find((w) => w.widget_token === saved) || ws[0];
                    setActiveToken(initial.widget_token);
                }
            } catch (e) { setError(formatApiError(e)); }
            setLoading(false);
        })();
    }, [user, navigate]);

    useEffect(() => {
        if (!activeToken) { setConfig(null); return; }
        localStorage.setItem("aria.active_widget", activeToken);
        (async () => {
            try {
                const { data } = await api.get(`/widget/${activeToken}/owner-config`);
                setConfig(data);
            } catch (e) {
                setConfig(null);
                setError(formatApiError(e));
            }
        })();
    }, [activeToken]);

    // Redirect /app/aria → /app/aria/console se nessun sub-path
    useEffect(() => {
        if (location.pathname === "/app/aria" || location.pathname === "/app/aria/") {
            navigate("/app/aria/console", { replace: true });
        }
    }, [location.pathname, navigate]);

    const refreshConfig = async () => {
        if (!activeToken) return;
        try {
            const { data } = await api.get(`/widget/${activeToken}/owner-config`);
            setConfig(data);
        } catch (e) { setError(formatApiError(e)); }
    };

    const planTier = useMemo(() => {
        if (sub?.is_lifetime) return "cinematic";
        return sub?.plan_tier || "free";
    }, [sub]);

    const ctxValue = { widgets, activeToken, setActiveToken, config, refreshConfig, sub, planTier, loading, error };

    if (loading) {
        return <div className="min-h-screen grid place-items-center" style={{ background: "#05050F", color: "#fff" }}>
            <div className="text-white/55 text-[12.5px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>caricamento area cliente…</div>
        </div>;
    }

    // Nessun widget creato — mostra solo se siamo su route che richiedono widget attivo
    const requiresActiveWidget = !["/app/aria/console", "/app/aria/account"].some(
        (p) => location.pathname === p || location.pathname.startsWith(p + "/")
    );
    if (widgets.length === 0 && requiresActiveWidget) {
        return (
            <div className="min-h-screen grid place-items-center px-6" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="aria-no-widgets">
                <div className="text-center max-w-md">
                    <div className="w-14 h-14 rounded-2xl mx-auto grid place-items-center mb-5" style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)" }}>
                        <Sparkles size={20} color="#fff" />
                    </div>
                    <h1 className="text-white text-[26px]" style={{ fontWeight: 300, letterSpacing: "-0.02em" }}>Crea la tua Aria</h1>
                    <p className="text-white/65 text-[14px] mt-3 mb-7" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Non hai ancora un&apos;istanza Aria. Crea la tua prima Aria personalizzata in 60 secondi.
                    </p>
                    <button type="button" onClick={() => navigate("/aria/crea")}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white"
                        style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)", fontWeight: 500 }}
                        data-testid="cta-create-first-aria">
                        <Plus size={14} /> Crea la mia Aria
                    </button>
                </div>
            </div>
        );
    }

    const activeWidget = widgets.find((w) => w.widget_token === activeToken);

    return (
        <AriaCustomerCtx.Provider value={ctxValue}>
            <div className="min-h-screen" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="aria-customer-area">
                <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", background: "radial-gradient(circle at 15% 5%, rgba(168,85,247,0.10) 0%, transparent 45%), radial-gradient(circle at 90% 90%, rgba(255,26,92,0.06) 0%, transparent 45%)" }} />

                {/* Top bar */}
                <header className="relative px-6 py-4 flex items-center justify-between border-b border-white/[0.06]">
                    <div className="flex items-center gap-3">
                        <button type="button" onClick={() => navigate("/app")} className="text-white/55 hover:text-white inline-flex items-center gap-1.5 text-[12px]" data-testid="back-workspace">
                            <ArrowLeft size={12} /> Workspace
                        </button>
                        <div className="w-px h-5 bg-white/10" />
                        <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)" }}>
                            <Sparkles size={14} color="#fff" />
                        </div>
                        <div>
                            <div className="text-white text-[14px]" style={{ fontWeight: 500 }}>Aria · Area Cliente</div>
                            <div className="text-white/45 text-[10.5px] uppercase tracking-[0.22em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                {TIER_LABEL[planTier] || "free"} plan
                            </div>
                        </div>
                    </div>

                    {/* Widget switcher */}
                    <div className="relative">
                        <button type="button" onClick={() => setSwitcherOpen((v) => !v)}
                            className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] transition border border-white/[0.08]"
                            data-testid="widget-switcher">
                            <span className="text-white/45 text-[10px] uppercase tracking-[0.2em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>widget</span>
                            <span className="text-white text-[13px]" style={{ fontWeight: 500 }}>{activeWidget?.site_name || activeWidget?.persona_name || "—"}</span>
                            <ChevronDown size={12} className="text-white/55" />
                        </button>
                        {switcherOpen && (
                            <div className="absolute right-0 mt-2 w-72 rounded-xl py-1.5 z-30" style={{ background: "#0d1018", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 20px 60px -10px rgba(0,0,0,0.5)" }} data-testid="widget-switcher-menu">
                                {widgets.map((w) => (
                                    <button key={w.widget_token} type="button"
                                        onClick={() => { setActiveToken(w.widget_token); setSwitcherOpen(false); }}
                                        className="w-full text-left px-3 py-2.5 hover:bg-white/[0.06] transition flex items-center gap-2.5"
                                        data-testid={`widget-option-${w.widget_token}`}>
                                        <div className="w-6 h-6 rounded-md grid place-items-center flex-none" style={{ background: TIER_COLOR[w.plan_tier || "free"] + "20", border: `1px solid ${TIER_COLOR[w.plan_tier || "free"]}40` }}>
                                            <Sparkles size={11} style={{ color: TIER_COLOR[w.plan_tier || "free"] }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white text-[13px] truncate">{w.site_name || w.persona_name}</div>
                                            <div className="text-white/45 text-[10.5px] truncate" style={{ fontFamily: "JetBrains Mono, monospace" }}>{w.site_url || w.widget_token?.slice(0, 12)}…</div>
                                        </div>
                                        {w.widget_token === activeToken && <Check size={12} className="text-white/65" />}
                                    </button>
                                ))}
                                <div className="h-px bg-white/[0.06] my-1" />
                                <button type="button" onClick={() => { setSwitcherOpen(false); navigate("/aria/crea"); }}
                                    className="w-full text-left px-3 py-2.5 hover:bg-white/[0.06] transition flex items-center gap-2 text-white/75 text-[12.5px]"
                                    data-testid="widget-option-create">
                                    <Plus size={12} /> Crea nuova Aria
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <div className="relative flex max-w-7xl mx-auto">
                    {/* Sidebar */}
                    <aside className="hidden md:block w-60 flex-none border-r border-white/[0.06] min-h-[calc(100vh-65px)] py-6 px-3">
                        <nav className="space-y-1" data-testid="aria-nav">
                            {NAV.map((n) => (
                                <NavLink key={n.to} to={n.to}
                                    className={({ isActive }) => `group flex items-start gap-3 px-3 py-2.5 rounded-lg transition ${isActive ? "bg-white/[0.06] text-white" : "text-white/65 hover:text-white hover:bg-white/[0.03]"}`}
                                    data-testid={`nav-${n.to.split("/").pop()}`}>
                                    {({ isActive }) => (
                                        <>
                                            <n.icon size={14} className={isActive ? "text-[#A855F7]" : "text-white/45"} style={{ marginTop: 2 }} />
                                            <div>
                                                <div className="text-[13px]" style={{ fontWeight: isActive ? 500 : 400 }}>{n.label}</div>
                                                <div className="text-[10.5px] text-white/40 mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>{n.desc}</div>
                                            </div>
                                        </>
                                    )}
                                </NavLink>
                            ))}
                        </nav>

                        {/* Mini status card piano */}
                        <div className="mt-6 p-3 rounded-xl" style={{ background: "rgba(15,18,28,0.55)", border: "1px solid rgba(255,255,255,0.06)" }}>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>Piano attivo</div>
                            <div className="text-white text-[14px] mt-1.5" style={{ fontWeight: 500 }}>{TIER_LABEL[planTier]}</div>
                            <div className="text-white/55 text-[11px] mt-0.5">
                                {sub?.is_lifetime ? `🏛️ Founder #${String(sub.founder_number || 0).padStart(3, "0")}`
                                    : sub?.status === "trialing" ? "in prova 15gg"
                                        : sub?.status === "active" ? "attivo"
                                            : "free per sempre"}
                            </div>
                        </div>
                    </aside>

                    {/* Outlet */}
                    <main className="flex-1 px-6 py-8 min-w-0">
                        {error && (
                            <div className="mb-4 p-3 rounded-xl text-[12px] flex items-start gap-2" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>
                                <AlertTriangle size={13} className="flex-none mt-0.5" /> {error}
                            </div>
                        )}
                        <Outlet />
                    </main>
                </div>
            </div>
        </AriaCustomerCtx.Provider>
    );
}
